import { API, API_BASE, toFullUrl } from '../config/api';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, KeyRound, Download, Loader2, Home, Music, ArrowLeft, FolderDown, ShieldCheck, Archive } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function MyPurchases() {
  const [step, setStep] = useState('email'); // email | code | purchases
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadedIds, setDownloadedIds] = useState(new Set());
  const [downloadingAll, setDownloadingAll] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/purchases/request-code`, { email: email.trim().toLowerCase() });
      setStep('code');
      toast.success('Verification code sent! Check your email.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send code';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/purchases/verify`, {
        email: email.trim().toLowerCase(),
        code: code.trim()
      });
      setPurchases(data.purchases || []);
      setStep('purchases');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid or expired code';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (purchase) => {
    setDownloadingId(purchase.id);
    try {
      const url = purchase.download_url.startsWith('/')
        ? `${API_BASE}${purchase.download_url}`
        : purchase.download_url;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${purchase.track_title}.mp3`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      setDownloadedIds(prev => new Set([...prev, purchase.id]));
      toast.success(`Downloaded: ${purchase.track_title}`);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    const downloadable = purchases.filter(p => p.has_download_file && p.download_url);
    if (downloadable.length === 0) {
      toast.error('No downloadable files available.');
      return;
    }
    setDownloadingAll(true);
    try {
      const tokens = downloadable.map(p => {
        const url = p.download_url;
        return url.split('/api/download/')[1];
      }).filter(Boolean);
      const response = await fetch(`${API}/purchases/download-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tokens })
      });
      if (!response.ok) throw new Error('Failed to create ZIP');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'My_Purchases.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      setDownloadedIds(new Set(downloadable.map(p => p.id)));
      toast.success(`Downloaded ${downloadable.length} track${downloadable.length !== 1 ? 's' : ''} as ZIP!`);
    } catch (err) {
      console.error('Download all error:', err);
      toast.error('Failed to download all. Try downloading individually.');
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="my-purchases-page">
      <div className="max-w-2xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-10">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[#737373] hover:text-[#E5E5E5] text-sm mb-6 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Home
          </Link>
          <h1 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#E5E5E5]">
            MY PURCHASES
          </h1>
          <p className="text-[#737373] mt-2">Access and re-download your purchased tracks.</p>
        </div>

        {/* Step 1: Email */}
        {step === 'email' && (
          <div className="bg-[#141414] border border-[#262626] p-8" data-testid="email-step">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-cabinet text-lg font-bold uppercase text-[#E5E5E5]">Verify Your Email</h2>
                <p className="text-xs text-[#737373]">Enter the email used during purchase</p>
              </div>
            </div>
            <form onSubmit={requestCode} className="flex flex-col gap-4">
              <Input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#0A0A0A] border-[#262626] text-[#E5E5E5] placeholder:text-[#525252] rounded-none h-12"
                data-testid="email-input"
              />
              <Button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none h-12"
                data-testid="send-code-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                {loading ? 'SENDING...' : 'SEND VERIFICATION CODE'}
              </Button>
            </form>
          </div>
        )}

        {/* Step 2: Code Verification */}
        {step === 'code' && (
          <div className="bg-[#141414] border border-[#262626] p-8" data-testid="code-step">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#DAA520] flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-cabinet text-lg font-bold uppercase text-[#E5E5E5]">Enter Code</h2>
                <p className="text-xs text-[#737373]">Check <strong className="text-[#A3A3A3]">{email}</strong> for a 6-digit code</p>
              </div>
            </div>
            <form onSubmit={verifyCode} className="flex flex-col gap-4">
              <Input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                className="bg-[#0A0A0A] border-[#262626] text-[#E5E5E5] placeholder:text-[#525252] rounded-none h-12 text-center text-2xl tracking-[0.5em] font-mono"
                data-testid="code-input"
              />
              <Button
                type="submit"
                disabled={loading || code.length < 6}
                className="w-full bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none h-12"
                data-testid="verify-code-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {loading ? 'VERIFYING...' : 'VERIFY & VIEW PURCHASES'}
              </Button>
              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); }}
                className="text-sm text-[#737373] hover:text-[#E5E5E5] transition-colors"
                data-testid="back-to-email-btn"
              >
                Use a different email
              </button>
            </form>
          </div>
        )}

        {/* Step 3: Purchases List */}
        {step === 'purchases' && (
          <div data-testid="purchases-step">
            {purchases.length === 0 ? (
              <div className="bg-[#141414] border border-[#262626] p-8 text-center">
                <Music className="w-12 h-12 mx-auto text-[#525252] mb-4" />
                <h2 className="font-cabinet text-lg font-bold uppercase text-[#E5E5E5] mb-2">No Purchases Found</h2>
                <p className="text-[#737373] text-sm mb-6">No downloadable purchases were found for this email.</p>
                <Link to="/tracks">
                  <Button className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="browse-tracks-btn">
                    BROWSE TRACKS
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-[#737373]">
                    <span className="text-[#E5E5E5] font-bold">{purchases.length}</span> purchase{purchases.length !== 1 ? 's' : ''} found for <span className="text-[#A3A3A3]">{email}</span>
                  </p>
                  <button
                    onClick={() => { setStep('email'); setEmail(''); setCode(''); setPurchases([]); setDownloadedIds(new Set()); }}
                    className="text-xs text-[#737373] hover:text-[#FF3B30] transition-colors"
                    data-testid="switch-email-btn"
                  >
                    Different email
                  </button>
                </div>
                {purchases.filter(p => p.has_download_file).length > 1 && (
                  <Button
                    onClick={handleDownloadAll}
                    disabled={downloadingAll}
                    className="w-full mb-4 bg-[#DAA520] hover:bg-white hover:text-[#0A0A0A] rounded-none h-11"
                    data-testid="download-all-btn"
                  >
                    {downloadingAll ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Archive className="w-4 h-4 mr-2" />
                    )}
                    {downloadingAll ? 'ZIPPING...' : `DOWNLOAD ALL (${purchases.filter(p => p.has_download_file).length} TRACKS)`}
                  </Button>
                )}
                <div className="flex flex-col gap-3">
                  {purchases.map((p) => (
                    <div key={p.id} className="bg-[#141414] border border-[#262626] p-4 flex items-center gap-4" data-testid={`purchase-item-${p.id}`}>
                      {/* Cover art */}
                      <div className="w-14 h-14 flex-shrink-0 bg-[#0A0A0A] overflow-hidden">
                        {p.cover_art ? (
                          <img src={toFullUrl(p.cover_art)} alt={p.track_title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Music className="w-6 h-6 text-[#525252]" />
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-cabinet font-bold uppercase text-[#E5E5E5] truncate">{p.track_title}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          {p.artist && <span className="text-xs text-[#737373]">{p.artist}</span>}
                          <span className="font-mono text-xs text-[#FF3B30]">${p.amount?.toFixed(2)}</span>
                          {p.purchased_at && (
                            <span className="text-xs text-[#525252]">
                              {new Date(p.purchased_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Download button */}
                      <Button
                        onClick={() => handleDownload(p)}
                        disabled={downloadingId === p.id || !p.has_download_file}
                        size="sm"
                        className={`rounded-none flex-shrink-0 ${
                          downloadedIds.has(p.id)
                            ? 'bg-[#22C55E] hover:bg-[#16A34A] text-white'
                            : 'bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A]'
                        }`}
                        data-testid={`download-purchase-${p.id}`}
                      >
                        {downloadingId === p.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : downloadedIds.has(p.id) ? (
                          <><FolderDown className="w-4 h-4 mr-1.5" /> SAVED</>
                        ) : (
                          <><Download className="w-4 h-4 mr-1.5" /> DOWNLOAD</>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
                {downloadedIds.size > 0 && (
                  <div className="mt-4 bg-[#0A0A0A] border border-[#22C55E]/30 p-4 text-left" data-testid="downloads-complete-notice">
                    <div className="flex items-center gap-2 mb-1">
                      <FolderDown className="w-4 h-4 text-[#22C55E]" />
                      <span className="text-sm font-bold text-[#22C55E]">Files Saved</span>
                    </div>
                    <p className="text-xs text-[#A3A3A3]">
                      Your files have been saved to your device's <strong className="text-[#E5E5E5]">Downloads</strong> folder.
                      Download links expire in 24 hours — come back anytime to generate new ones.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-8 flex justify-center">
          <Link to="/">
            <Button variant="outline" className="rounded-none border-[#262626] hover:border-[#FF3B30]" data-testid="home-btn">
              <Home className="w-4 h-4 mr-2" />
              BACK TO HOME
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
